// src/sections/Piani.jsx — the plans screen (route /billing, menu label "Piani"; also the Piani tab of Gestione
// Workspace via `variant='tab'`).
//
// ⭐ ONE READ BUILDS THIS PAGE: GET /billing/subscription (billing/useSubscriptionRead.js). The backend decides
// which button each row gets (`action`) and why a row has none (`reason_code`); ⛔ this file never infers either
// from prices, plan ids or dates.
//
// ⛔ CUSTOMER-ADMIN ONLY (or a superadmin outside admin mode). 403 CUSTOMER_ADMIN_REQUIRED / 409 NO_CUSTOMER hide
// the screen ("not for you"), never an error. Errors apiFetch already handled (tenant picker, sign-out) say nothing.
//
// ⛔⛔ v203 — CONFIRM FIRST, IN A MODAL, WORDED BY US. Every purchase, plan change, cancellation, resume and
// «Annulla il cambio» opens BillingConfirmDialog with OUR sentences from the row's / screen's `disclosure_codes`
// (billing/confirmContent.js). A purchase opens its Stripe session only AFTER the confirm (opening one already
// retires leftover subscriptions); if the session answers DIFFERENT codes the confirm opens again before the
// browser leaves. ⛔ No server text anywhere: not `disclosure` (gone), `reason`, `packs_reason`, nor the `message`
// of /cancel /resume /cancel-change. The «crediti extra acquistati» sentence (PURCHASED_CREDITS_KEPT) is said
// only when /credits/balance shows purchased credits (> 0); unknown → said (fail open).
//
// ⭐ OUTCOMES ARE A MODAL (components/ui/OutcomeDialog.jsx), worded from the RESPONSE (`status`, `timing`,
// `released_scheduled_plan_id`) and opened after the first re-read. Errors are a warning outcome (refusalCopy),
// with the remedy as its action (card update, invoicing details). Stored as data and worded at RENDER time, so a
// language switch re-words them.
//
// ⭐ WEBHOOK LAG: /cancel (scheduled), /resume, an immediate change and an immediate cancel reach GET seconds later;
// `settle(predicate)` re-reads (3 s × 10) with the actions disabled, then leaves a quiet note if still behind. Right
// after «Riprendi» a plan change can still answer 409 CANCELLATION_PENDING: within RESUME_GRACE_MS it is worded
// "still landing".
//
// ⛔ NOTHING LANDS AT REDIRECT TIME. After Stripe, /billing/return and /credits?topup=done poll /credits/balance.
// ⭐ THE GRID IS GROUPED (v173) — see PlanGrid / billing/planGroups.js.
// ⭐ v181 `variant` / `basePath` / `billingDetailsTo` — the Piani tab. Defaults = the standalone /billing page. Only
// the "nothing happened" returns (checkout/pack cancel, portal close) follow `basePath`; the checkout SUCCESS
// return stays /billing/return (marked `&from=console` from the tab, so it links back into the console) and the
// pack success return stays /credits?topup=done. The ?checkout= / ?portal= cleanup navigates to the CURRENT path.
//
// ⛔⛔ v204 «PASSA ORA» IS NEVER SENT WITHOUT A PREVIEW (billing/useChangeNow.js): it charges the saved card with no
// Stripe page, so the confirmation shows the charge (ChargeSummary), the card and a button naming the total, and the
// change is sent with the preview's quote; a moved quote previews again and re-confirms. No usable card → the
// button opens the card update. A failed change (402 ×2, 409 PAYMENT_PENDING, 502 CHANGE_FAILED_CHANGE_RELEASED, the
// preview's refusals) RE-READS FIRST, then opens its outcome, so the outcome names the plans from the fresh screen
// (a scheduled change released before the charge does not come back). Stripe's payment link (3-D Secure) opens in a
// NEW TAB (billing/stripeLink.js); the first return to this tab after opening it waits for `payment_pending` to clear.
// ⭐ The card update (Stripe portal) opens in a NEW TAB too: the tab is opened EMPTY inside the click (a tab opened
// after the await would be popup-blocked), detached (`opener = null`), and pointed at the portal once its URL
// arrives; blocked anyway → this tab, as before. The first return to this tab after opening it re-reads fresh.
// «Aggiorna il metodo di pagamento» and «Disdici» are chips next to the card title (BillingTitleChips).
// ⭐ v221 THE TERMS BOX: a first purchase (`checkout`) and a pack (`pack`) confirm with legal/TermsCheckbox inside
// BillingConfirmDialog — the terms are read every time it opens, «Acquista» stays disabled until the box is ticked,
// and the session carries exactly the versions shown. A 409 TERMS_VERSION_CHANGED keeps the dialog OPEN with the new
// texts (from the 409 body), unticked, amber note — nothing was created on Stripe. The re-confirmation after changed
// codes (`pending.url`) has no box: that session is open and its acceptance already recorded.
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  createCheckoutSession, cancelScheduledChange, cancelSubscription, resumeSubscription,
  createPackSession, createPortalSession, absoluteUrl,
} from '../billing/billingApi.js';
import { refusalCopy, isHandledElsewhere } from '../billing/refusalCopy.js';
import { confirmContent, sameCodes, lacksUsableCard } from '../billing/confirmContent.js';
import useSubscriptionRead, { HIDE_CODES } from '../billing/useSubscriptionRead.js';
import useChangeNow from '../billing/useChangeNow.js';
import { openInNewTab, safeHttpsUrl } from '../billing/stripeLink.js';
import { writeTopUpBaseline } from '../billing/topUpBaseline.js';
import { fetchCreditBalance } from '../services/api/creditsApi.js';
import { formatRenewalDate, parseDecimal } from '../utils/creditsFormat.js';
import { HIDDEN_REASON_CODES } from '../components/billing/PlanCard.jsx';
import PlanGrid from '../components/billing/PlanGrid.jsx';
import CurrentPlanPanel from '../components/billing/CurrentPlanPanel.jsx';
import PacksPanel from '../components/billing/PacksPanel.jsx';
import BillingConfirmDialog from '../components/billing/BillingConfirmDialog.jsx';
import ChargeSummary from '../components/billing/ChargeSummary.jsx';
import BillingTitleChips from '../components/billing/BillingTitleChips.jsx';
import CancelReasonFields from '../components/billing/CancelReasonFields.jsx';
import OutcomeDialog from '../components/ui/OutcomeDialog.jsx';
import SectionCard, { PAGE_COLUMN, CARD_BODY } from '../components/ui/SectionCard.jsx';
import useCurrentTerms from '../legal/useCurrentTerms.js';
import TermsCheckbox from '../legal/TermsCheckbox.jsx';
import { isTermsConflict } from '../legal/termsModel.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';

// Refusals whose remedy is "re-read the plans screen" (the row went stale).
const REREAD_CODES = new Set([
  'NO_SUBSCRIPTION', 'UNKNOWN_PLAN', 'UNKNOWN_PACK', 'NO_SCHEDULED_CHANGE', 'PLAN_UNCHANGED',
  'SUBSCRIPTION_ALREADY_ACTIVE', 'CUSTOMER_NOT_INVOICEABLE', 'NOT_A_STRIPE_SUBSCRIPTION',
  'PLAN_NOT_SELLABLE', 'PLAN_NOT_PURCHASABLE', 'PACK_REQUIRES_PAID_PLAN',
  'CANCELLATION_PENDING', 'CANCEL_FAILED_CHANGE_RELEASED',
]);
// v204: refusals of a plan change (or of its preview) after which the screen is re-read BEFORE the outcome opens.
const CHANGE_REREAD_CODES = new Set([
  'PAYMENT_ACTION_REQUIRED', 'PAYMENT_NOT_COMPLETED', 'PAYMENT_PENDING', 'CHANGE_FAILED_CHANGE_RELEASED',
  'CHANGE_NOT_IMMEDIATE', 'QUOTE_EXPIRED', 'QUOTE_INVALID',
]);
// Outcomes whose remedy is Stripe's payment link (when it passes the https check).
const PAYMENT_LINK_CODES = new Set(['PAYMENT_ACTION_REQUIRED', 'PAYMENT_PENDING']);
/** Re-reads after a return from Stripe's payment tab (× SETTLE_POLL_MS). */
const RETURN_POLLS = 4;
/** How long after a successful «Riprendi» a 409 CANCELLATION_PENDING means "the resume is still landing". */
export const RESUME_GRACE_MS = 60_000;
const NO_REASON = { reasonCode: null, comment: '' };

export default function Piani({ variant = 'page', basePath = '/billing', billingDetailsTo = '/billing-details' }) {
  const { t } = useTranslation('billing');
  const { isSuperadmin, adminMode, isCustomerAdmin, activeTenant } = useAuth();
  const tenantId = activeTenant?.tenant_id;
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const bounced = !!(isSuperadmin && adminMode);
  const allowed = !bounced && !!(isCustomerAdmin || isSuperadmin);

  // Query flags only SEED state; the URL is cleaned right after (see below).
  const [returnFlags] = useState(() => {
    const q = new URLSearchParams(search);
    return { cancelled: q.get('checkout') === 'cancelled', portal: q.get('portal') === 'closed' };
  });

  const { data, loading, loadError, hidden, settling, lagging, reload, settle, hide } = useSubscriptionRead();
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  // v221: the purchase's terms box — read when its confirmation opens, idle otherwise.
  const needsTerms = (pending?.kind === 'checkout' || pending?.kind === 'pack') && !pending?.url;
  const terms = useCurrentTerms(needsTerms);
  const [reason, setReason] = useState(NO_REASON);
  const [outcome, setOutcome] = useState(() => (returnFlags.cancelled ? { tone: 'info', key: 'checkoutCancelled' } : null));
  const resumedAt = useRef(0);
  const changeNow = useChangeNow(tenantId);
  // What this page sent the user to in another tab: 'payment' (Stripe's payment link) or 'portal' (card update);
  // the next return to this tab re-reads. null = nothing.
  const awayFor = useRef(null);
  // Does the company hold purchased credits? null = not known. Read once per workspace (purchases leave the page).
  const [purchasedHeld, setPurchasedHeld] = useState(null);

  useEffect(() => {
    if (!allowed || !tenantId) return undefined;
    let current = true;   // a stale answer (workspace switched) never lands
    setPurchasedHeld(null);
    (async () => {
      try {
        const b = await fetchCreditBalance({ tenant: tenantId });
        const n = parseDecimal(b?.purchased_remaining);
        if (current) setPurchasedHeld(n == null ? null : n > 0);
      } catch {
        if (current) setPurchasedHeld(null);   // fail open: the sentence stays
      }
    })();
    return () => { current = false; };
  }, [allowed, tenantId]);

  useEffect(() => {
    if (!allowed) return;
    // Back from Stripe's card page: bypass the 60 s browser cache.
    reload({ fresh: returnFlags.portal });
  }, [allowed, reload, returnFlags, tenantId]);

  // Back from Stripe's payment tab: wait for the confirmed payment to land (webhook), once per opened link.
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState === 'hidden' || !awayFor.current) return;
      const away = awayFor.current;
      awayFor.current = null;
      // Quiet and short: the user may not have paid, so no lag note (the banner keeps saying what waits).
      if (away === 'payment') settle((d) => !d.payment_pending, { quiet: true, maxPolls: RETURN_POLLS });
      else reload({ fresh: true });
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [settle, reload]);

  // A workspace switch drops the open confirmation (it belongs to the other company) and the pending tab return.
  useEffect(() => {
    setPending(null);
    awayFor.current = null;
  }, [tenantId]);

  // Drop ?checkout= / ?portal= so a reload does not replay the notice. ⛔ The CURRENT path, never '/billing'.
  useEffect(() => {
    if (returnFlags.cancelled || returnFlags.portal) navigate(pathname, { replace: true });
  }, [returnFlags, navigate, pathname]);

  if (bounced) return <Navigate to="/admin/users" replace />;
  if (!allowed || hidden) return <NotForYou t={t} variant={variant} />;

  // ── errors: close the confirm, report in the outcome modal ───────────────
  const fail = async (err) => {
    setPending(null);
    setBusy(false);
    if (HIDE_CODES.has(err?.code)) { hide(); return; }
    if (isHandledElsewhere(err)) return;
    const report = { tone: 'warning', err, justResumed: Date.now() - resumedAt.current < RESUME_GRACE_MS };
    if (CHANGE_REREAD_CODES.has(err?.code)) {
      // The outcome names plans from the fresh screen; busy meanwhile, so nothing else starts under it.
      setBusy(true);
      await reload({ fresh: true });
      setBusy(false);
      setOutcome(report);
      return;
    }
    setOutcome(report);
    if (REREAD_CODES.has(err?.code)) reload({ fresh: true });
  };

  const openPaymentLink = (url) => { if (openInNewTab(url)) awayFor.current = 'payment'; };

  // ── opening a confirmation (nothing is sent yet) ──────────────────────────
  const open = (next) => { setOutcome(null); setReason(NO_REASON); setPending(next); };
  // «Passa ora» asks the backend what it would charge before anything opens; the page is busy meanwhile.
  const askChangeNow = async (option) => {
    setOutcome(null);
    setBusy(true);
    try {
      const next = await changeNow.preview(option);
      setBusy(false);
      if (next) open(next);
    } catch (err) { fail(err); }
  };
  const askPlan = (option) => (option.action === 'change-now' ? askChangeNow(option) : open({
    kind: option.action === 'start-subscription' ? 'checkout' : 'change',
    option, codes: option.disclosure_codes || [], discloses: option.discloses || [],
  }));
  const askPack = (pack) => open({ kind: 'pack', pack, codes: data?.packs_disclosure_codes || [] });
  const askCancel = () => open({ kind: 'cancel', codes: data?.cancel_disclosure_codes || [] });
  const askResume = () => open({ kind: 'resume', codes: [] });
  const scheduledRow = (data?.options || []).find((o) => o.plan_id === data?.scheduled_plan_id);
  const askCancelChange = () => open({
    kind: 'cancelChange', codes: [], planName: scheduledRow?.display_name || data?.scheduled_plan_id,
  });

  // /credits?topup=done measures the RISE against a baseline taken BEFORE leaving (billing/topUpBaseline.js).
  const stashTopUpBaseline = async () => {
    if (!tenantId) return;
    try {
      const b = await fetchCreditBalance({ tenant: tenantId, fresh: true });
      writeTopUpBaseline(tenantId, b?.remaining_credits);
    } catch { /* degrade to the neutral return notice */ }
  };

  // ── the writes, AFTER the confirm ─────────────────────────────────────────
  const successPath = (planId) =>
    `/billing/return?plan=${encodeURIComponent(planId)}${variant === 'tab' ? '&from=console' : ''}`;

  const toStripe = async (p) => {
    if (p.kind === 'pack') await stashTopUpBaseline();
    window.location.assign(p.url);   // hand over to Stripe; stay busy
  };

  const openSession = async (p) => {
    const cancelUrl = absoluteUrl(`${basePath}?checkout=cancelled`);
    // ⚠️ Can take several seconds (retires a lapsed subscription): the busy state is the pending indicator.
    const s = p.kind === 'pack'
      ? await createPackSession({ packId: p.pack.pack_id, successUrl: absoluteUrl('/credits?topup=done'), cancelUrl, terms: terms.terms })
      : await createCheckoutSession({ planId: p.option.plan_id, successUrl: absoluteUrl(successPath(p.option.plan_id)), cancelUrl, terms: terms.terms });
    if (!sameCodes(s.disclosure_codes, p.codes)) {
      // The terms moved since the page loaded: confirm AGAIN before the browser leaves (the session stays open).
      setPending({ ...p, codes: s.disclosure_codes || [], url: s.url, changed: true });
      setBusy(false);
      return;
    }
    await toStripe({ ...p, url: s.url });
  };

  const doChange = async (p) => {
    const sent = await changeNow.submit(p);
    if ('requote' in sent) {
      // The quote expired or the total moved: the same confirmation, with the new figure (nothing was charged).
      setBusy(false);
      setPending(sent.requote);
      return;
    }
    const r = sent.response;
    setPending(null);
    setBusy(false);
    if (r?.timing === 'IMMEDIATE') {
      await settle((d) => d.plan_id === p.option.plan_id);
      setOutcome({ tone: 'success', key: 'changeApplied' });
    } else {
      await reload({ fresh: true });   // SCHEDULED is written synchronously
      setOutcome({ tone: 'info', key: 'outcome.changeScheduled', vars: { plan: p.option.display_name } });
    }
  };

  // ⛔ TWO OUTCOMES — branch on `status`, never on cancel_at_period_end.
  const doCancel = async () => {
    const r = await cancelSubscription({ reasonCode: reason.reasonCode, comment: reason.comment });
    setPending(null);
    setBusy(false);
    const immediate = r?.status === 'canceled';
    await settle(immediate ? (d) => !d.plan_id : (d) => d.cancel_at_period_end === true);
    setOutcome({
      tone: immediate ? 'warning' : 'info',
      key: immediate ? 'outcome.cancelImmediate' : 'outcome.cancelScheduled',
      extraKey: r?.released_scheduled_plan_id ? 'outcome.changeReleased' : null,
    });
  };

  const doResume = async () => {
    await resumeSubscription();
    resumedAt.current = Date.now();
    setPending(null);
    setBusy(false);
    await settle((d) => d.cancel_at_period_end !== true);
    setOutcome({ tone: 'success', key: 'outcome.resumed' });
  };

  const doCancelChange = async () => {
    await cancelScheduledChange();
    setPending(null);
    setBusy(false);
    await reload({ fresh: true });   // written synchronously
    setOutcome({ tone: 'success', key: 'outcome.changeCancelled' });
  };

  const confirm = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      if (pending.kind === 'checkout' || pending.kind === 'pack') {
        if (pending.url) await toStripe(pending);
        else await openSession(pending);
        return;
      }
      if (pending.kind === 'change' && lacksUsableCard(pending)) {
        setPending(null);
        await openPortal();
        return;
      }
      if (pending.kind === 'change') await doChange(pending);
      else if (pending.kind === 'cancel') await doCancel();
      else if (pending.kind === 'resume') await doResume();
      else if (pending.kind === 'cancelChange') await doCancelChange();
    } catch (err) {
      if (isTermsConflict(err)) {
        // v221: new texts while the dialog was open — keep it open, new links, unticked (nothing created on Stripe).
        terms.applyConflict(err);
        setBusy(false);
        return;
      }
      fail(err);
    }
  };

  // ⛔ Must run its window.open INSIDE the click (no await before it), or the browser blocks the tab.
  const openPortal = async () => {
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;   // the portal page gets no handle on this one
    setBusy(true);
    try {
      const { url } = await createPortalSession({ returnUrl: absoluteUrl(`${basePath}?portal=closed`) });
      if (!tab) { window.location.assign(url); return; }   // blocked: this tab, as before (stay busy)
      tab.location.href = url;
      awayFor.current = 'portal';
      setBusy(false);
    } catch (err) {
      if (tab) tab.close();
      fail(err);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────
  if (loading && !data) return <Shell t={t} variant={variant}><p style={{ fontSize: 13 }}>{t('loading')}</p></Shell>;
  if (!data) {
    return (
      <Shell t={t} variant={variant}>
        <Notice tone="error">{refusalCopy(t, loadError)}</Notice>
        <button type="button" onClick={() => reload({ fresh: true })} style={secondaryBtn}>{t('retry')}</button>
      </Shell>
    );
  }

  const rows = (data.options || []).filter((o) => !HIDDEN_REASON_CODES.has(o.reason_code));
  const invoiceIncomplete = (data.missing_invoice_fields || []).length > 0;
  const locked = busy || settling;
  const content = pending ? confirmContent(t, pending, {
    purchasedHeld, currentPlanName: data.plan_display_name, today: formatRenewalDate(new Date().toISOString()),
  }) : null;
  const planName = (id) => (data.options || []).find((o) => o.plan_id === id)?.display_name;

  return (
    <Shell t={t} variant={variant} titleAside={
      <BillingTitleChips data={data} busy={locked} onUpdateCard={openPortal} onCancel={askCancel} />
    } after={
      // ⛔ `packs_reason` is the server's English: the panel words the CODE itself.
      <PacksPanel packs={data.packs} available={!!data.packs_available} reasonCode={data.packs_reason_code}
                  busy={locked} onBuy={askPack} billingDetailsTo={billingDetailsTo} />
    }>
      {invoiceIncomplete && (
        <InvoiceGate t={t} fields={data.missing_invoice_fields} billingDetailsTo={billingDetailsTo} />
      )}

      <CurrentPlanPanel
        data={data} busy={locked} settling={settling} lagging={lagging}
        currentHasCard={rows.some((o) => o.plan_id === data.plan_id)}
        scheduledPlanName={scheduledRow?.display_name}
        scheduledDate={formatRenewalDate(data.scheduled_at)}
        onResume={askResume} onCancelChange={askCancelChange} onUpdateCard={openPortal}
        onConfirmPayment={openPaymentLink} pendingPlanName={planName(data.payment_pending?.plan_id)}
      />

      {/* ⛔ A toggle SELECTS a server row — the `plan_id` handed to onAct is always the displayed row's own. */}
      <PlanGrid rows={rows} currentPlanId={data.plan_id} busy={locked} billingDetailsTo={billingDetailsTo} onAct={askPlan} />

      <BillingConfirmDialog
        open={!!pending} title={content?.title} lines={content?.lines || []} confirmLabel={content?.confirmLabel}
        busy={busy} confirmDisabled={needsTerms && !terms.canProceed}
        onConfirm={confirm} onClose={() => setPending(null)}>
        {content?.charge && <ChargeSummary rows={content.charge} />}
        {pending?.kind === 'cancel' && <CancelReasonFields value={reason} onChange={setReason} disabled={busy} />}
        {needsTerms && <TermsCheckbox terms={terms} disabled={busy} />}
      </BillingConfirmDialog>

      <OutcomeDialog
        outcome={outcome && {
          tone: outcome.tone,
          text: outcomeText(t, outcome, { data, planName }),
          action: outcomeAction(t, outcome, { data, openPortal, openPaymentLink, navigate, billingDetailsTo }),
        }}
        onClose={() => setOutcome(null)} />
    </Shell>
  );
}

// ── local helpers ────────────────────────────────────────────────────────────

/** The outcome's sentence, worded at render time (plan names from the screen as it is NOW — re-read after a failed change). */
function outcomeText(t, outcome, { data, planName }) {
  if (outcome.err) {
    return refusalCopy(t, outcome.err, {
      justResumed: outcome.justResumed,
      linkUsable: !!safeHttpsUrl(outcome.err.hosted_invoice_url),
      releasedPlanName: planName(outcome.err.released_scheduled_plan_id),
      currentPlanName: data?.plan_display_name,
    });
  }
  return [t(outcome.key, outcome.vars), outcome.extraKey ? t(outcome.extraKey) : null].filter(Boolean).join(' ');
}

/** The remedy an error outcome offers as its secondary button (never reached by Esc). */
function outcomeAction(t, outcome, { data, openPortal, openPaymentLink, navigate, billingDetailsTo }) {
  const code = outcome.err?.code;
  // 3-D Secure: Stripe's page, in a new tab — only for a link that passes the https check.
  const link = safeHttpsUrl(outcome.err?.hosted_invoice_url);
  if (PAYMENT_LINK_CODES.has(code) && link) {
    return { label: t('current.confirmPayment'), onClick: () => openPaymentLink(link) };
  }
  // ⛔ 402 PAYMENT_NOT_COMPLETED: nothing changed — a card update, not a retry.
  if (code === 'PAYMENT_NOT_COMPLETED' && data?.subscription_id) {
    return { label: t('current.updateCard'), onClick: openPortal };
  }
  if (code === 'CUSTOMER_NOT_INVOICEABLE') {
    return { label: t('plan.completeInvoiceDetails'), onClick: () => navigate(billingDetailsTo) };
  }
  return undefined;
}

// ⭐ ONE heading per render path, and the console owns the <h1>: inside the tab
// the page title is an <h2>. Every path that can paint — main, loading, error and
// NotForYou (reached by a 403 INSIDE the tab too) — goes through here.
// ⭐ 2026-10-08: the console tabs' look — a SectionCard (icon, title, subtitle) in the 1280px column; the plan grid
// lives in its body. `after` renders BELOW the card (the «Crediti extra» card).
function Shell({ t, variant = 'page', children, after = null, titleAside = null }) {
  return (
    <div style={PAGE_COLUMN}>
      <SectionCard icon={Receipt} title={t('title')} subtitle={t('subtitle')} headingLevel={variant === 'tab' ? 2 : 1}
                   titleAside={titleAside}>
        <div style={CARD_BODY}>{children}</div>
      </SectionCard>
      {after}
    </div>
  );
}

const TONE = { info: 'info', success: 'verde', warning: 'attenzione', error: 'errore' };

function Notice({ tone = 'info', children }) {
  const c = SEMANTIC[TONE[tone]] || SEMANTIC.info;
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} style={{
      marginBottom: 16, padding: '10px 14px', borderRadius: RADII.controlInner,
      background: c.bg, color: c.fg, fontSize: 12,
    }}>{children}</div>
  );
}

function NotForYou({ t, variant = 'page' }) {
  return (
    <Shell t={t} variant={variant}>
      <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>{t('notForYou')}</p>
    </Shell>
  );
}

function InvoiceGate({ t, fields, billingDetailsTo = '/billing-details' }) {
  const c = SEMANTIC.attenzione;
  return (
    <div role="status" style={{
      marginBottom: 16, padding: '14px 16px', borderRadius: RADII.control,
      background: c.bg, color: COLORS.ink, fontSize: 13,
    }}>
      <strong style={{ display: 'block', marginBottom: 4 }}>{t('invoiceGate.title')}</strong>
      <p style={{ margin: '0 0 6px' }}>{t('invoiceGate.body')}</p>
      <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
        {fields.map((f) => (
          <li key={f}>{t(`admin:credits.notInvoiceable.fields.${f}`, { defaultValue: f })}</li>
        ))}
      </ul>
      <Link to={billingDetailsTo} style={{ color: COLORS.accent, fontWeight: 600 }}>
        {t('invoiceGate.cta')}
      </Link>
    </div>
  );
}

const secondaryBtn = {
  padding: '8px 14px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.border}`,
  background: COLORS.surface, color: COLORS.ink, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: FONT_SANS,
};
